import { __mediamax_api_url_config__ } from '@/constants/appConfig'
import apiClient from '../../shared/apiClient'
import { platformHeaders } from '@/shared/platformHeaders'
import type { ApiError } from '../../shared/apiClient'
import type {
  BrandDetailsResponse,
  MetaLinkApiError,
  LinkBrandRequest,
  LinkBrandResponse,
  CheckLinkStatusResponse,
  VerifyViewAccessRequest,
  VerifyViewAccessResponse,
} from './types'

export async function getBrandDetailsApi(
  brandId: number,
  platformId: string
): Promise<BrandDetailsResponse> {
  try {
    const response = await apiClient.get<BrandDetailsResponse>(
      __mediamax_api_url_config__.getBrandDetails,
      {
        skipForbiddenRedirect: true,
        headers: platformHeaders(platformId, brandId),
      }
    )

    return response
  } catch (error) {
    if (error && typeof error === 'object' && 'message' in error) {
      const apiError = error as ApiError
      throw {
        message: apiError.message || 'Failed to fetch brand details',
        status: apiError.status,
        code: apiError.code,
      } as MetaLinkApiError
    }
    throw error
  }
}

export async function linkBrandApi(
  brandId: number,
  platformId: string,
  businessId: string
): Promise<LinkBrandResponse> {
  try {
    const response = await apiClient.post<LinkBrandResponse>(
      __mediamax_api_url_config__.linkBrand,
      {
        businessId,
      } as LinkBrandRequest,
      {
        headers: platformHeaders(platformId, brandId),
      }
    )

    return response
  } catch (error) {
    if (error && typeof error === 'object' && 'message' in error) {
      const apiError = error as ApiError
      throw {
        message: apiError.message || 'Failed to link brand',
        status: apiError.status,
        code: apiError.code,
      } as MetaLinkApiError
    }
    throw error
  }
}

export async function checkLinkStatusApi(
  brandId: number,
  platformId: string
): Promise<CheckLinkStatusResponse> {
  try {
    const response = await apiClient.get<CheckLinkStatusResponse>(
      __mediamax_api_url_config__.checkLinkStatus,
      {
        headers: platformHeaders(platformId, brandId),
      }
    )

    return response
  } catch (error) {
    if (error && typeof error === 'object' && 'message' in error) {
      const apiError = error as ApiError
      throw {
        message: apiError.message || 'Failed to check link status',
        status: apiError.status,
        code: apiError.code,
      } as MetaLinkApiError
    }
    throw error
  }
}

export async function verifyViewAccessApi(
  brandId: number,
  platformId: string,
  payload: VerifyViewAccessRequest
): Promise<VerifyViewAccessResponse> {
  try {
    const response = await apiClient.post<unknown, VerifyViewAccessRequest>(
      __mediamax_api_url_config__.verifyViewAccess,
      payload,
      {
        headers: platformHeaders(platformId, brandId),
      }
    )
    if (!response || typeof response !== 'object') {
      throw { message: 'Invalid verify view access response' } as MetaLinkApiError
    }

    const raw = response as {
      deeplink?: string
      blinkit_link?: string
    }

    return {
      deeplink: raw.deeplink ?? '',
      blinkitLink: raw.blinkit_link ?? '',
    }
  } catch (error) {
    if (error && typeof error === 'object' && 'message' in error) {
      const apiError = error as ApiError
      throw {
        message: apiError.message || 'Failed to verify view access',
        status: apiError.status,
        code: apiError.code,
      } as MetaLinkApiError
    }
    throw error
  }
}
